import React, { useEffect, useState } from "react";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import { Box, Button, IconButton, TextField, Tooltip, Typography, MenuItem, CircularProgress, Alert, Chip, Divider } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RoleFormModal from "../RoleFormModal/RoleFormModal";
import Swal from "sweetalert2";
import { useAuth } from "../../context/authContext";
import TabTitle from "../TabTitle/TabTitle";
import UserName from "../UserName/UserName";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import { getRoles } from "../../services/createEditUSerFormService";
import { getRoleUsers,deleteRoles,duplicateRole } from "../../services/manageRolesService";
const defaultRoles = [1, 2, 3, 4];
const defaultRoleNames = new Set(["admin", "manager", "purchaser", "viewer"]);
const normalizeRoles = (payload, fallback = []) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.roles)) return payload.roles;
    return Array.isArray(fallback) ? fallback : [];
};

const normalizeRolePermissions = (permissions) => {
    if (!permissions) return {};
    if (typeof permissions === "string") {
        try {
            const parsed = JSON.parse(permissions);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch (_) {
            return {};
        }
    }
    return permissions && typeof permissions === "object" ? permissions : {};
};

const normalizeRoleRows = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((row) => ({
        ...row,
        permissions: normalizeRolePermissions(row?.permissions),
    }));
const isRoleRowsPayload = (payload) =>
    Array.isArray(payload) && payload.every((row) => row && row.role_id !== undefined);

const ManageRoles = ({ showHeader = true }) => {
    const [roles, setRoles] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRole, setSelectedRole] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [rolesPendingDelete, setRolesPendingDelete] = useState([]);
    const [rolesPendingDeleteNames, setRolesPendingDeleteNames] = useState([]);
    const [reassignUsersByRole, setReassignUsersByRole] = useState({});
    const [reassignOptionsByRole, setReassignOptionsByRole] = useState({});
    const [reassignSelections, setReassignSelections] = useState({});
    const [reassignMessage, setReassignMessage] = useState("");
    const [reassignNextSteps, setReassignNextSteps] = useState([]);
    const [reassignError, setReassignError] = useState("");
    const [viewMode, setViewMode] = useState(false);
    const [viewUsers, setViewUsers] = useState([]);
    const [userModalState, setUserModalState] = useState({
        open: false,
        role: null,
        users: [],
        loading: false,
    });
    const { hasPermission, user } = useAuth();
    const { t } = useTranslation();
    const isSuperAdmin = Number(user?.user_id) === 1;
    const canCreateRoles = isSuperAdmin || hasPermission('role_management.create_roles');
    const canEditRoles = isSuperAdmin || hasPermission('role_management.edit_roles');
    const canDeleteRoles = isSuperAdmin || hasPermission('role_management.delete_roles');

    const replaceRoles = (payload) => {
        setRoles((prev) => normalizeRoleRows(normalizeRoles(payload, prev)));
    };

    useEffect(() => {
        const fetchRoles = async () => {
            setLoading(true);
            try {
                const response = await getRoles();
                console.log("In manageroles getroles response", response);
                const payload = response?.data ?? response?.roles ?? response;
                if (response?.status || Array.isArray(payload) || Array.isArray(payload?.roles)) {
                    // Show all roles in the datagrid
                    replaceRoles(payload);
                } else {
                    console.warn("ManageRoles: unexpected roles response shape", response);
                    replaceRoles([]);
                }
            } catch (error) {
                console.log(error);
                replaceRoles([]);
            } finally {
                setLoading(false);
            }
        };
        fetchRoles();
    }, []);

    const extractUsers = (resp) => {
        const candidates = resp?.data?.users || resp?.users || resp?.data || [];
        if (Array.isArray(candidates)) return candidates;
        if (Array.isArray(candidates.users)) return candidates.users;
        return [];
    };

    const normalizeUserRows = (usersList = []) =>
        usersList.map((user, index) => ({
            ...user,
            user_id: user?.user_id ?? user?.id ?? user?.userId ?? `user-${index}`,
            user_display_name: user?.user_display_name || user?.user_name || user?.display_name || user?.name || user?.user_email || `User ${index + 1}`,
            user_role_name: user?.user_role_name || user?.role_name || user?.role || "",
            user_email: user?.user_email || user?.email || "",
            user_status: user?.user_status ?? user?.status ?? "",
        }));

    const isProtectedRole = (roleRow) => {
        if (!roleRow) return false;
        if (roleRow?.is_default_role === true) return true;
        const idBlocked = defaultRoles.includes(Number(roleRow.role_id));
        const nameBlocked = defaultRoleNames.has(String(roleRow.role_name || "").trim().toLowerCase());
        return idBlocked || nameBlocked;
    };

    const blockedRoleNamesText = (roleRows = []) => {
        const names = Array.from(
            new Set(
                (roleRows || [])
                    .filter((r) => isProtectedRole(r))
                    .map((r) => String(r?.role_name || "").trim())
                    .filter(Boolean)
            )
        );
        if (names.length === 0) return "";
        if (names.length === 1) return names[0];
        return names.join(", ");
    };

    const closeReassignModal = () => {
        setReassignOpen(false);
        setRolesPendingDelete([]);
        setRolesPendingDeleteNames([]);
        setReassignUsersByRole({});
        setReassignOptionsByRole({});
        setReassignSelections({});
        setReassignMessage("");
        setReassignNextSteps([]);
        setReassignError("");
    };

    const openReassignModal = (resp, pendingIds = [], pendingNames = [], existingSelections = null) => {
        const detail = Array.isArray(resp?.data?.roles) ? resp.data.roles : [];
        const usersByRole = {};
        detail.forEach((item) => {
            const rid = Number(item?.role_id);
            usersByRole[rid] = normalizeUserRows(item?.users || []);
        });
        const idsFromResponse = detail.map((d) => Number(d.role_id)).filter(Boolean);
        const finalPendingIds = idsFromResponse.length > 0 ? idsFromResponse : pendingIds.map((id) => Number(id));
        const namesFromResponse = finalPendingIds
            .map((rid) => roles.find((r) => Number(r.role_id) === rid)?.role_name)
            .filter(Boolean);
        const finalPendingNames = namesFromResponse.length > 0 ? namesFromResponse : pendingNames;
        const optionsByRole = {};
        const serverByRole = resp?.data?.reassign_options_by_role || {};
        const common = Array.isArray(resp?.data?.common_reassign_options) ? resp.data.common_reassign_options : [];
        finalPendingIds.forEach((rid) => {
            const list = Array.isArray(serverByRole?.[rid]) ? serverByRole[rid] : common;
            optionsByRole[rid] = list || [];
        });
        const initialSelections = {};
        finalPendingIds.forEach((rid) => {
            const previous = existingSelections?.[rid];
            const hasPreviousInOptions = (optionsByRole[rid] || []).some((o) => Number(o.role_id) === Number(previous));
            initialSelections[rid] = hasPreviousInOptions ? Number(previous) : "";
        });
        setRolesPendingDelete(finalPendingIds);
        setRolesPendingDeleteNames(finalPendingNames);
        setReassignUsersByRole(usersByRole);
        setReassignOptionsByRole(optionsByRole);
        setReassignSelections(initialSelections);
        setReassignMessage(resp?.message || t('BUTTON.ACTION_REQUIRED_TEXT'));
        setReassignNextSteps(Array.isArray(resp?.data?.next_steps) ? resp.data.next_steps : []);
        setReassignOpen(true);
    };

    const handleModalClose = () => {
        setSelectedRows([]);
        setIsEditing(false);
        setModalOpen(false);
        setViewUsers([]);
    };

    const handleRowSelect = (rows) => {
        setSelectedRows(rows);
    };

    const handleEditRole = (row) => {
        if (isProtectedRole(row) && !isSuperAdmin) {
            Swal.fire({
                icon: "info",
                title: "Default Roles Are Protected",
                text: `Only super admin can edit default role: ${row?.role_name || "this role"}. Duplicate it to create a custom role instead.`,
            });
            return;
        }
        const roleId = Number(row?.role_id);
        const fullRole = roles.find((r) => Number(r?.role_id) === roleId) || row;
        const safeRole = JSON.parse(JSON.stringify(fullRole || {}));
        console.log("Editing Role: ", safeRole);
        setSelectedRows([]);
        setIsEditing(true);
        setSelectedRole(safeRole);
        setViewMode(false);
        setModalOpen(true);
    };

    const handleCreateNewRole = () => {
        setSelectedRows([]);
        setSelectedRole({});
        setIsEditing(false);
        setViewMode(false);
        setModalOpen(true);
    };

    const handleRowClick = async (params) => {
        const row = params.row;
        const roleId = Number(row?.role_id);
        const fullRole = roles.find((r) => Number(r?.role_id) === roleId) || row;
        const safeRole = JSON.parse(JSON.stringify(fullRole || {}));
        setSelectedRows([]);
        setSelectedRole(safeRole);
        setIsEditing(true);
        setViewMode(true);
        setModalOpen(true);
        try {
            const resp = await getRoleUsers(roleId);
            console.log("In manageroles getroleusers response", resp);
            if (resp?.status) setViewUsers(normalizeUserRows(extractUsers(resp)));
        } catch (e) {
            console.error('Failed to load role users', e);
            setViewUsers([]);
        }
    };
    const handleDeleteSelected = async () => {
        if (selectedRows.length === 0) {
            Swal.fire({
                icon: "warning",
                title: t('GENERAL.NO_ROLES_SELECTED'),
                text: t('GENERAL.PLEASE_SELECT_AT_LEAST_ONE_ROLE_TO_DELETE'),
            });
            return;
        }

        const selectedRowIds = selectedRows.map((id) => Number(id));
        const selectedRoleRows = roles.filter((r) => selectedRowIds.includes(Number(r.role_id)));
        const hasRestrictedRoles = selectedRoleRows.some((r) => isProtectedRole(r));

        if (hasRestrictedRoles) {
            const blockedNames = blockedRoleNamesText(selectedRoleRows);
            Swal.fire({
                icon: "info",
                title: "Default Roles Are Protected",
                text: blockedNames
                    ? `You cannot delete default role(s): ${blockedNames}. Deselect these roles and try again.`
                    : t('GENERAL.YOU_ARE_NOT_AUTHORIZED_TO_DELETE_ONE_OR_MORE_OF_THE_SELECTED_ROLES'),
            });
            return;
        }

        // ✅ Add confirmation before delete
        const confirmResult = await Swal.fire({
            title: "Confirm Deletion",
            text: `Are you sure you want to delete the selected ${selectedRows.length} role(s)? This action cannot be undone.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText:t('BUTTON.CANCEL'),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            reverseButtons: true,
        });

        if (!confirmResult.isConfirmed) return;

        try {
            const response = await deleteRoles(selectedRowIds);
            if (response?.data?.in_use && response?.data?.need_reassign) {
                const reassignIds = Array.isArray(response?.data?.roles)
                    ? response.data.roles.map((r) => Number(r.role_id))
                    : selectedRowIds;
                const pendingNames = roles
                    .filter((r) => reassignIds.includes(Number(r.role_id)))
                    .map((r) => r.role_name);
                openReassignModal(response, reassignIds, pendingNames);
            } else if (response.status && isRoleRowsPayload(response?.data)) {
                replaceRoles(response?.data);
                setSelectedRows([]);
                Swal.fire({
                    title: "Deleted!",
                    text: t('GENERAL.SELECTED_ROLES_HAVE_BEEN_SUCCESSFULLY_DELETED'),
                    icon: "success",
                });
            } else {
                Swal.fire({
                    icon: "info",
                    title: t('BUTTON.ACTION_REQUIRED_TITLE'),
                    text: response?.message || t('BUTTON.ACTION_REQUIRED_TEXT'),
                });
            }
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: t('GENERAL.AN_ERROR_OCCURRED_WHILE_DELETING_THE_SELECTED_ROLES_PLEASE_TRY_AGAIN'),
            });
        }
    };


    const handleDuplicateRole = async (row) => {
        setSelectedRows([]);
        const { value: newName } = await Swal.fire({
            title: `${t('GENERAL.DUPLICATE_ROLE_1')} ${row.role_name}`,
            input: 'text',
            inputLabel: t('USER_MANAGEMENT.NEW_ROLE_NAME'),
            inputValue: `${row.role_name} Copy`,
            showCancelButton: true,
            inputValidator: (val) => !val?.trim() ? t('UI_TEXT.NAME_REQUIRED') : undefined,
        });
        if (!newName) return;
        try {
            const resp = await duplicateRole(row.role_id, newName.trim());
            console.log("In manageroles duplicaterole response", resp);
            if (resp.status) {
                replaceRoles(resp?.data);
                Swal.fire({ icon: 'success', title: t('GENERAL.ROLE_DUPLICATED') });
            } else {
                Swal.fire({ icon: 'error', title: 'Failed', text: resp.message || t('GENERAL.COULD_NOT_DUPLICATE_ROLE') });
            }
        } catch (e) {
            console.error(e);
            Swal.fire({ icon: 'error', title: 'Error', text: t('GENERAL.ERROR_DUPLICATING_ROLE') });
        }
    };

    const confirmReassign = async () => {
        if (rolesPendingDelete.length === 0) return;
        const mapping = {};
        for (const rid of rolesPendingDelete) {
            const selected = Number(reassignSelections?.[rid]);
            if (!selected) {
                setReassignError("Please select a replacement role for each role before confirming delete.");
                return;
            }
            mapping[rid] = selected;
        }
        try {
            const resp = await deleteRoles(rolesPendingDelete, null, mapping);
            console.log("In manageroles deleteroles response", resp);
            if (resp?.data?.need_reassign) {
                setReassignError(resp?.message || "Reassignment is still required. Please review selections.");
                openReassignModal(resp, rolesPendingDelete, rolesPendingDeleteNames, reassignSelections);
            } else if (resp?.status && !resp?.data?.need_reassign) {
                if (isRoleRowsPayload(resp?.data)) {
                    replaceRoles(resp?.data);
                } else {
                    const fresh = await getRoles();
                    if (fresh?.status) replaceRoles(fresh?.data);
                }
                setSelectedRows([]);
                closeReassignModal();
                Swal.fire({ icon: 'success', title: t('GENERAL.ROLE_DELETED_AND_USERS_REASSIGNED') });
            } else {
                setReassignError(resp?.message || t('GENERAL.COULD_NOT_DELETE_ROLE'));
                Swal.fire({ icon: 'error', title: 'Failed', text: resp?.message || t('GENERAL.COULD_NOT_DELETE_ROLE') });
            }
        } catch (e) {
            console.error(e);
            setReassignError(t('GENERAL.ERROR_PROCESSING_REQUEST'));
            Swal.fire({ icon: 'error', title: 'Error', text: t('GENERAL.ERROR_PROCESSING_REQUEST') });
        }
    };

    const handleDeleteRole = async (row) => {
        if (isProtectedRole(row)) {
            Swal.fire({
                icon: "info",
                title: "Default Role Is Protected",
                text: `You cannot delete default role: ${row?.role_name || "this role"}.`,
            });
            return;
        }
        // ✅ Confirmation before deleting a specific role
        const confirmResult = await Swal.fire({
            title: t('BUTTON.CONFIRM_ROLE_DELETION_TITLE'),
            text: t('BUTTON.CONFIRM_ROLE_DELETION_TEXT', {roleName: row.role_name}),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t('BUTTON.YES_DELETE_CONFIRM'),
            cancelButtonText: t('BUTTON.CANCEL'),
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            reverseButtons: true,
        });

        if (!confirmResult.isConfirmed) return;

        try {
            const resp = await deleteRoles([row.role_id]);
            console.log("In manageroles deleteroles response", resp);
            if (resp?.data?.in_use && resp?.data?.need_reassign) {
                openReassignModal(resp, [row.role_id], [row.role_name]);
            } else if (resp.status && isRoleRowsPayload(resp?.data)) {
                replaceRoles(resp?.data);
                setSelectedRows([]);
                Swal.fire({
                    icon: "success",
                    title: t('BUTTON.ROLE_DELETED_TITLE'),
                    text: t('BUTTON.ROLE_DELETED_TEXT', {roleName: row.role_name}),
                });
            } else {
                Swal.fire({
                    icon: "info",
                    title: t('BUTTON.ACTION_REQUIRED_TITLE'),
                    text:
                        resp?.message ||
                        t('BUTTON.ACTION_REQUIRED_TEXT'),
                });
            }
        } catch (e) {
            console.error(e);
            Swal.fire({
                icon: "error",
                title: t('BUTTON.DELETION_FAILED_TITLE'),
                text: t('BUTTON.DELETION_FAILED_TEXT'),
            });
        }
    };


    const userColumns = [
        { field: 'user_display_name', headerName: t('TABLE_HEADERS.NAME'), flex: 1.5 },
        { field: 'user_email', headerName: t('TABLE_HEADERS.EMAIL'), flex: 2 },
        { field: 'user_role_name', headerName: t('LABELS.ROLE'), flex: 1 },
        {
            field: 'user_status',
            headerName: t('TABLE_HEADERS.STATUS'),
            flex: 0.8,
            valueFormatter: (value) => (value === 0 ? 'Pending' : 'Approved'),
        },
    ];

    const handleOpenUserModal = async (roleRow) => {
        if (!roleRow?.role_id) return;
        setSelectedRows([]);
        setUserModalState({
            open: true,
            role: roleRow,
            users: [],
            loading: true,
        });
        try {
            const resp = await getRoleUsers(roleRow.role_id);
            const usersList = normalizeUserRows(extractUsers(resp));
            setUserModalState({
                open: true,
                role: roleRow,
                users: usersList,
                loading: false,
            });
        } catch (error) {
            console.error("Failed to load role users", error);
            setUserModalState({
                open: true,
                role: roleRow,
                users: [],
                loading: false,
            });
        }
    };

    const handleCloseUserModal = () => {
        setUserModalState({
            open: false,
            role: null,
            users: [],
            loading: false,
        });
    };

    const columns = [
        {
            field: "role_name",
            headerName: t('UI_TEXT.ROLE_NAME'),
            flex: 1,
            getSearchValue: (params) => params?.value || "",
            renderCell: (params) => (
                <Tooltip title={t("UI_TEXT.TOOLTIP_VIEW_ROLE_DETAILS")}>
                    <span
                        style={{
                            cursor: "pointer",
                            fontWeight: "normal",
                        }}
                        onMouseEnter={(e) => (e.target.style.fontWeight = "bold")}
                        onMouseLeave={(e) => (e.target.style.fontWeight = "normal")}>
                        {params.value}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "role_type",
            headerName: t('GENERAL.ROLE_TYPE'),
            width: 200,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "role_category",
            headerName: t('GENERAL.TYPE', { defaultValue: 'Type' }),
            width: 160,
            align: "center",
            headerAlign: "center",
            valueGetter: (_, row) => (row?.is_default_role ? 'Default' : 'Custom'),
            getSearchValue: (params) => params?.row?.is_default_role ? 'Default' : 'Custom',
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.row?.is_default_role ? 'Default' : 'Custom'}
                    color={params.row?.is_default_role ? 'default' : 'primary'}
                    variant={params.row?.is_default_role ? 'filled' : 'filled'}
                    sx={
                        params.row?.is_default_role
                            ? {
                                backgroundColor: '#6b7280',
                                color: '#fff',
                            }
                            : undefined
                    }
                />
            ),
        },
        {
            field: "users_count",
            headerName: t('GENERAL.USERS_1'),
            width: 200,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Button
                    variant="text"
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleOpenUserModal(params.row);
                    }}
                >
                    {params.value ?? 0}
                </Button>
            ),
        },
        {
            field: "actions",
            headerName: t('TABLE_HEADERS.ACTIONS'),
            width: 200,
            sortable: false,
            headerAlign: "center",
            align: "center",
            searchable: false,
            renderCell: (params) => {
                const editDisabled = isProtectedRole(params.row) && !isSuperAdmin;
                const deleteDisabled = isProtectedRole(params.row) || !canDeleteRoles;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
                            <Tooltip title={t("UI_TEXT.TOOLTIP_EDIT_ROLE")}>
                                <span>
                                    {canEditRoles && (
                                        <IconButton
                                            color="primary"
                                            aria-label="Edit Role"
                                            disabled={editDisabled}
                                            onClick={(event) => { event.stopPropagation(); handleEditRole(params.row); }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </span>
                            </Tooltip>
                            <Tooltip title={t("UI_TEXT.TOOLTIP_DUPLICATE_ROLE")}>
                                <span>
                                    {canCreateRoles && (
                                        <IconButton
                                            color="primary"
                                            aria-label={t('USER_MANAGEMENT.DUPLICATE_ROLE_1')}
                                            onClick={(event) => { event.stopPropagation(); handleDuplicateRole(params.row); }}
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    )}
                                </span>
                            </Tooltip>
                            <Tooltip title={deleteDisabled ? t('GENERAL.CANNOT_DELETE_THIS_ROLE') : "Delete Role"}>
                                <span>
                                    {!deleteDisabled && (
                                        <IconButton
                                            color="error"
                                            aria-label="Delete Role"
                                            onClick={(event) => { event.stopPropagation(); handleDeleteRole(params.row); }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                );
            },
        },
    ];

    var customButtons = [
        ...(canCreateRoles ? [{
            label: t('GENERAL.CREATE_NEW_ROLE'),
            onClick: () => {
                handleCreateNewRole();
            },
            icon: <AddCircleIcon />,
        }] : []),
        ...(canDeleteRoles ? [{
            label: t('LABELS.DELETE_SELECTED'),
            color: "error",
            onClick: () => {
                handleDeleteSelected();
            },
            icon: <DeleteIcon />,
            disabled: !selectedRows || selectedRows.length === 0,
        }] : []),
    ];

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2 }}>
                {showHeader && <TabTitle title={t('ITEMS_TEXT.ROLES')} subtitle={t('UI_TEXT.MANAGE_USER_ROLES')} />}
                {loading ? (
                    <DatagridLoading actionButtons={2} columns={4} rows={6} />
                ) : (
                    <div>
                        <Datagrid
                            key="role_id"
                            data={roles}
                            checkboxSelection={true}
                            rowSelectionModel={selectedRows}
                            customButtons={customButtons.flat()}
                            columns={columns}
                            onSelect={handleRowSelect}
                            rowClick={true}
                            onRowClick={handleRowClick}
                            rowIdField="role_id"
                            sortBy={[{ field: "role_name", sort: "asc" }]}
                            sx={{ height: "400px" }}
                            loading={loading}
                                hideFilterFromColumnMenu
                                lockActiveColumnInManageColumns
                                preventHidingLastColumn
                        />
                    </div>
                )}
            </Box>

            <RoleFormModal
                open={modalOpen}
                allRoles={roles}
                onClose={handleModalClose}
                role={isEditing ? selectedRole : {}}
                setRole={setSelectedRole}
                setAllRoles={replaceRoles}
                viewOnly={viewMode}
                allowEdit={hasPermission('role_management.edit_roles') && (isSuperAdmin || !selectedRole?.is_default_role)}
                usersForRole={viewUsers}
            />

            {/* Reassign Modal */}
            <ModalWrapper
                open={reassignOpen}
                setOpen={closeReassignModal}
                title={`${t('TITLES.DELETE_ROLE')}: ${rolesPendingDeleteNames.join(', ') || ''}`}
                content={
                    <>
                        {reassignError && (
                            <Alert severity="error" sx={{ mb: 1.5 }}>
                                {reassignError}
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                            {reassignMessage || t('UI_TEXT.REASSIGN_ROLE_PROMPT')}
                        </Typography>
                        <Box
                            sx={{
                                mb: 1.5,
                                p: 1.25,
                                border: "1px solid #e7e7e7",
                                borderRadius: 1.5,
                                backgroundColor: "#fafafa",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                alignItems: "center",
                            }}
                        >
                            <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`Roles to delete: ${rolesPendingDelete.length}`}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Users affected: ${rolesPendingDelete.reduce((sum, rid) => sum + ((reassignUsersByRole?.[rid] || []).length), 0)}`}
                            />
                            <Typography variant="caption" color="text.secondary">
                                Choose a replacement role for each section below.
                            </Typography>
                        </Box>
                        <Box sx={{ mt: 1, maxHeight: '45vh', overflowY: 'auto', pr: 1 }}>
                            {rolesPendingDelete.map((rid) => {
                                const roleRow = roles.find((r) => Number(r.role_id) === Number(rid));
                                const roleName = roleRow?.role_name || `Role ${rid}`;
                                const roleType = roleRow?.role_type || "Unknown";
                                const users = reassignUsersByRole?.[rid] || [];
                                const options = reassignOptionsByRole?.[rid] || [];
                                return (
                                    <Box
                                        key={`reassign-${rid}`}
                                        sx={{
                                            mb: 2,
                                            p: 1.5,
                                            border: '1px solid #e8e8e8',
                                            borderRadius: 1.5,
                                            backgroundColor: "#fff",
                                            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                {roleName}
                                            </Typography>
                                            <Box sx={{ display: "flex", gap: 0.75 }}>
                                                <Chip size="small" variant="outlined" label={`Type: ${roleType}`} />
                                                <Chip size="small" variant="outlined" label={`Users: ${users.length}`} />
                                            </Box>
                                        </Box>
                                        <Divider sx={{ mb: 1 }} />
                                        <TextField
                                            select
                                            fullWidth
                                            label="Reassign To"
                                            size="small"
                                            value={reassignSelections?.[rid] || ""}
                                            onChange={(e) => {
                                                setReassignSelections((prev) => ({ ...prev, [rid]: Number(e.target.value) }));
                                                if (reassignError) setReassignError("");
                                            }}
                                            disabled={options.length === 0}
                                            sx={{ mb: 1 }}
                                        >
                                            {options.map((r) => (
                                                <MenuItem key={r.role_id} value={r.role_id}>{r.role_name}</MenuItem>
                                            ))}
                                        </TextField>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
                                            Only compatible replacement roles are listed. Users will be reassigned before deletion.
                                        </Typography>
                                        {options.length === 0 && (
                                            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                                                {t('UI_TEXT.NO_COMPATIBLE_ROLE_FOR_REASSIGN', { defaultValue: "No compatible replacement role is available." })}
                                            </Typography>
                                        )}
                                        <Box sx={{ mt: 1, maxHeight: '18vh', overflowY: 'auto', px: 1 }}>
                                            {users.map((u, idx) => (
                                                <Box key={`${u.user_id || u.user_email || idx}-${rid}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", py: 0.75, borderBottom: '1px solid #f3f3f3' }}>
                                                    <UserName name={u?.user_display_name} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('LABELS.EMAIL')}: {u.user_email}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            {users.length === 0 && (
                                                <Typography variant="caption" color="text.secondary">{t('UI_TEXT.NO_USERS_FOUND')}</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                            {rolesPendingDelete.length > 0 && rolesPendingDelete.every((rid) => (reassignOptionsByRole?.[rid] || []).length === 0) && (
                                <Box sx={{ mt: 1 }}>
                                    {reassignNextSteps.map((step, idx) => (
                                        <Typography key={`${step}-${idx}`} variant="caption" display="block" color="text.secondary">
                                            {idx + 1}. {step}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </>
                }
                actions={
                    <>
                        <Button variant="outlined" onClick={closeReassignModal} sx={{ mr: 1 }}>{t('BUTTON.CANCEL')}</Button>
                        <Button
                            variant="contained"
                            disabled={
                                rolesPendingDelete.length === 0 ||
                                rolesPendingDelete.some((rid) => (reassignOptionsByRole?.[rid] || []).length === 0) ||
                                rolesPendingDelete.some((rid) => !reassignSelections?.[rid])
                            }
                            onClick={confirmReassign}
                        >
                            {t('BUTTON.CONFIRM_DELETE')}
                        </Button>
                    </>
                }
            />
            <ModalWrapper
                open={userModalState.open}
                setOpen={() => {}}
                onClose={handleCloseUserModal}
                title={
                    userModalState.role
                        ? `${userModalState.role.role_name || ""} ${t('LABELS.USERS') || "Users"}`
                        : t('LABELS.USERS') || "Users"
                }
                content={
                    userModalState.loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Datagrid
                            data={userModalState.users}
                            columns={userColumns}
                            checkboxSelection={false}
                            rowIdField="user_id"
                            showSearchBar={true}
                            autoHeight={false}
                            gridHeight={420}
                                hideFilterFromColumnMenu
                                lockActiveColumnInManageColumns
                                preventHidingLastColumn
                        />
                    )
                }
                actions={
                    <Button onClick={handleCloseUserModal}>{t('BUTTON.CLOSE') || 'Close'}</Button>
                }
            />
        </>
    );
};

export default ManageRoles;
